import React, { useState, useEffect } from 'react';
import { NavLink, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const Layout = () => {
  const { user, gym, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [insightsExpanded, setInsightsExpanded] = useState(location.pathname.startsWith('/insights'));
  
  // Dark mode state with localStorage persistence
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const saved = localStorage.getItem('darkMode');
    return saved ? JSON.parse(saved) : false;
  });

  // Apply dark mode class to body
  useEffect(() => {
    document.body.classList.toggle('dark-mode', isDarkMode);
    localStorage.setItem('darkMode', JSON.stringify(isDarkMode));
  }, [isDarkMode]);

  const toggleTheme = () => {
    setIsDarkMode(!isDarkMode);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const isInsightsActive = location.pathname.startsWith('/insights');
  const isAdmin = user?.role === 'admin';

  // Filter insights sub-items based on role (Actions removed - moved to Follow-up)
  const insightsSubItems = [
    { path: '/insights/revenue', icon: '💰', label: 'Revenue Insights', adminOnly: true },
    { path: '/insights/members', icon: '👥', label: 'Member Insights', adminOnly: false },
    { path: '/insights/renewals', icon: '🔔', label: 'Renewal Insights', adminOnly: false }
  ].filter(item => !item.adminOnly || isAdmin);

  return (
    <div className={`layout ${isDarkMode ? 'dark-mode' : ''}`}>
      <aside className="sidebar">
        <div className="sidebar-header">
          <h2>💪 {gym?.gymName || 'GymKhata.com'}</h2>
          <span>Management System</span>
        </div>
        
        <nav className="sidebar-nav">
          <NavLink to="/" end className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}>
            📊 Dashboard
          </NavLink>
          <NavLink to="/members" className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}>
            👥 Members
          </NavLink>
          <NavLink to="/payments" className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}>
            💳 Payments
          </NavLink>
          <NavLink to="/leads" className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}>
            🎯 Leads
          </NavLink>
          <NavLink to="/followup" className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}>
            📋 Follow-up
          </NavLink>
          <NavLink to="/expenses" className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}>
            💰 Expenses
          </NavLink>
          
          {/* Insights with Collapsible Tree */}
          <div className="nav-tree">
            <button 
              className={`nav-link nav-tree-toggle ${isInsightsActive ? 'active' : ''}`}
              onClick={() => setInsightsExpanded(!insightsExpanded)}
              style={{
                width: '100%',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                background: isInsightsActive ? 'linear-gradient(90deg, rgba(102, 126, 234, 0.3) 0%, rgba(118, 75, 162, 0.1) 100%)' : 'transparent',
                border: 'none',
                cursor: 'pointer',
                borderLeft: isInsightsActive ? '3px solid #667eea' : '3px solid transparent'
              }}
            >
              <span>📈 Insights</span>
              <span style={{ 
                fontSize: '10px', 
                transition: 'transform 0.2s',
                transform: insightsExpanded ? 'rotate(90deg)' : 'rotate(0deg)'
              }}>▶</span>
            </button>
            
            <div className="nav-tree-children" style={{
              maxHeight: insightsExpanded ? '200px' : '0',
              overflow: 'hidden',
              transition: 'max-height 0.3s ease-in-out',
              paddingLeft: '15px'
            }}>
              {insightsSubItems.map(item => (
                <NavLink 
                  key={item.path}
                  to={item.path} 
                  className={({ isActive }) => `nav-link nav-sub-link ${isActive ? 'active' : ''}`}
                  style={{ fontSize: '14px', padding: '12px 16px' }}
                >
                  {item.icon} {item.label}
                </NavLink>
              ))}
            </div>
          </div>

          {/* Staff - Admin Only */}
          {isAdmin && (
            <NavLink to="/staff" className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}>
              👨‍💼 Staff
            </NavLink>
          )}

          {/* Settings - Admin Only */}
          {isAdmin && (
            <NavLink to="/settings" className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}>
              ⚙️ Settings
            </NavLink>
          )}

          {/* Support */}
          <NavLink to="/support" className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}>
            🎧 Support
          </NavLink>

          {/* Attendance */}
          <NavLink to="/attendance" className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}>
            📋 Attendance
          </NavLink>

          {/* Invoice */}
          <NavLink to="/invoice" className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}>
            🧾 Invoice
          </NavLink>
        </nav>

        <div className="sidebar-footer">
          {/* Role Badge */}
          <div style={{
            padding: '8px 12px',
            marginBottom: '10px',
            background: isAdmin ? 'linear-gradient(135deg, #764ba2, #667eea)' : 'linear-gradient(135deg, #667eea, #5a67d8)',
            borderRadius: '20px',
            textAlign: 'center',
            fontSize: '11px',
            fontWeight: 'bold',
            color: 'white',
            textTransform: 'uppercase',
            letterSpacing: '1px',
            boxShadow: '0 2px 10px rgba(102, 126, 234, 0.3)'
          }}>
            {isAdmin ? '👑 Admin' : '👤 Staff'}
          </div>
          <div className="user-info">
            Logged in as: <strong>{user?.name || user?.email}</strong>
          </div>
          <button className="logout-btn" onClick={handleLogout}>
            Logout
          </button>
        </div>
      </aside>

      <main className="main-content">
        {/* Top Bar with Theme Toggle */}
        <div className="top-bar">
          <div className="theme-switch">
            <span className="theme-switch-label">{isDarkMode ? '🌙 Dark' : '☀️ Light'}</span>
            <div 
              className={`theme-toggle ${isDarkMode ? 'active' : ''}`}
              onClick={toggleTheme}
              title={isDarkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
            >
              <div className="theme-toggle-slider">
                {isDarkMode ? '🌙' : '☀️'}
              </div>
            </div>
          </div>
        </div>
        <Outlet />
      </main>
    </div>
  );
};

export default Layout;
